import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { Box } from "@mui/material";

import {
  useLocation,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "@/store";
import { fetchPropertySectionById } from "@/store/propertySectionSlice";
import {
  fetchContractHistoryByUnit,
  fetchMoreContractHistoryByUnit,
} from "@/store/contractHistorySlice";
import { deletePropertySectionDocument } from "@/store/propertySectionSlice";
import {
  fetchContractsByUnit,
  createContract,
  updateContract,
  endContract,
  renewContract,
  searchActiveContractsByCustomer,
} from "@/store/contractSlice";
import { updateCustomer } from "@/store/customerSlice";
import { fetchUnitById } from "@/store/unitSlice";
import { resolveAreaTsubo, resolveIsAncillaryUnit } from "@/utils/areaTsubo";
import { useRefetchOn } from "@/hooks/useRefetchOn";
import { fetchPropertyDetail } from "@/store/propertySlice";
import {
  CustomSectionTitle,
  CustomLoading,
  CustomFileModal,
  PropertyRegistrationTabs,
  usePreviewDrawerUnsaved,
} from "@ippon/shared-components";
import SectionTabForm from "../../../../components/PropertyDetail/shared/SectionTabForm";
import {
  extractS3KeyFromUrl,
  getApiBaseUrl,
  getContentTypeFromFileName,
  processJsonResponse,
  createBlobAndDownload,
} from "@ippon/shared-utils";
import { getAuthToken } from "@/utils/authUtils";
import { type DepartureProcessFormData } from "../../../../components/PropertyDetail/PrivateArea/modal/DepartureProcess";
import { encodeSectionDocumentPayloads } from "@/utils/encodeSectionDocumentPayloads";
import PaymentHistoryTab from "../../../../components/PropertyDetail/shared/PaymentHistoryTab";
import { useForm } from "react-hook-form";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import InitialLoadError from "@/components/ErrorState/InitialLoadError";
import type { ApiErrorPayload, ApiErrorType } from "@/utils/apiErrorPayload";
import {
  shouldShowInitialLoadError,
  useStaleInitialLoadErrorToast,
} from "@/utils/initialLoadError";
import ContractorTab from "./components/ContractorTab";
import type { Customer } from "@ippon/shared-types";
import ContractHistoryTab from "./components/ContractHistoryTab";
import RepairsSectionInline from "./components/RepairsSectionInline";
import DetailPageHeader from "./components/DetailPageHeader";
import DetailPageModals from "./components/DetailPageModals";
import LinkedCustomerEditConfirmModal, {
  type LinkedContractSummary,
} from "./components/LinkedCustomerEditConfirmModal";
import {
  type PrivateAreaTabKey,
  type PrivateAreaTenantSubTabKey,
  DEFAULT_PRIVATE_AREA_TAB_INDEX,
  DEFAULT_PRIVATE_AREA_TENANT_SUBTAB_INDEX,
  PRIVATE_AREA_TENANT_SUBTAB_KEYS,
  PRIVATE_AREA_TENANT_SUBTABS,
  getPrivateAreaTabConfig,
  getPrivateAreaTabKeys,
  getPrivateAreaTabs,
  isStrictlyInvalidPrivateAreaTabQuery,
  isValidPrivateAreaTabQuery,
  parseTabId,
} from "./tabConfig";
import {
  type PrivateAreaDetailState,
  type PrivateAreaHistoryRecord,
  formatDisplayDateTime,
} from "./types";

interface PrivateAreaDetailPageProps {
  /** ドロワー埋め込み等で URL params より優先される ID */
  overridePropertyId?: string;
  overridePrivateAreaId?: string;
  embedded?: boolean;
  /** embedded 時の初期タブ index (未指定は既定タブ)。search params の代わり。 */
  overrideInitialTab?: number;
  /** embedded 時の初期サブタブ index (テナント管理タブ用)。 */
  overrideInitialSubTab?: number;
  /** embedded 時に、全画面遷移用の現在タブ状態を親ドロワーへ通知する。 */
  onEmbeddedTabChange?: (state: { tab: number; subtab?: number }) => void;
}

const PrivateAreaDetailPage: React.FC<PrivateAreaDetailPageProps> = ({
  overridePropertyId,
  overridePrivateAreaId,
  embedded = false,
  overrideInitialTab,
  overrideInitialSubTab,
  onEmbeddedTabChange,
}) => {
  const navigate = useNavigate();
  const urlParams = useParams<{ propertyId: string; privateAreaId: string }>();
  // 埋め込み時は override を優先
  const params = {
    propertyId: overridePropertyId || urlParams?.propertyId,
    privateAreaId: overridePrivateAreaId || urlParams?.privateAreaId,
  } as { propertyId?: string; privateAreaId?: string };
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();
  const [initialLoadError, setInitialLoadError] = useState<ApiErrorType | null>(
    null,
  );
  const current = useSelector(
    (s: RootState) => s.propertySections.current.data,
  );
  // Unit / Contract をそれぞれ Redux から取得
  const unit = useSelector((s: RootState) => s.units.current.data);
  const contractsByUnitEntry = useSelector(
    (s: RootState) => s.contracts.byUnit[params.privateAreaId || ""],
  );
  const contractsByUnit = contractsByUnitEntry?.data || [];
  // /contracts?unit_id=X&status=active で取得しているので status==="active" のみが入る前提。
  // 念のため再度フィルタして null にフォールバック。
  const activeContract = useMemo(
    () => contractsByUnit.find((c) => c.status === "active") ?? null,
    [contractsByUnit],
  );
  const areaTsubo = resolveAreaTsubo(unit, current);
  // PropertySection の loading のみでページ全体のローディング判定。
  // ContractHistory はタブを開いた時にしか fetch しないので、ページ初期表示の
  // ローディング指標には使わない (= 永遠に loading=true になるのを防ぐ)。
  const loading = useSelector(
    (s: RootState) => s.propertySections.current.loading,
  );
  const state = (location.state || {}) as PrivateAreaDetailState;

  const decodeDocumentNameFromUrl = (url: string) => {
    try {
      const parsed = new URL(String(url));
      const raw = parsed.pathname.split("/").pop() || "";
      return decodeURIComponent(raw).replace(/^\d{13}_\d+_/, "");
    } catch {
      const withoutQuery = String(url).split("?")[0];
      const raw = withoutQuery.split("/").pop() || withoutQuery;
      try {
        return decodeURIComponent(raw).replace(/^\d{13}_\d+_/, "");
      } catch {
        return raw.replace(/^\d{13}_\d+_/, "");
      }
    }
  };

  // 物件データを取得
  const propertyData = useSelector(
    (state: RootState) => (state.property as any).detailed?.data,
  );

  // 契約履歴は ContractHistory slice (UnitIndex GSI 経由) のみを参照。
  // legacy unit.contract.contractor_details の fallback は廃止。
  const contractHistorySlice = useSelector((s: RootState) => s.contractHistory);
  const contractHistoryByUnit = params.privateAreaId
    ? contractHistorySlice.byUnit[params.privateAreaId]?.data
    : undefined;
  const contractHistoryHasMore = params.privateAreaId
    ? !!contractHistorySlice.byUnit[params.privateAreaId]?.hasMore
    : false;
  const contractHistoryLoading = params.privateAreaId
    ? !!contractHistorySlice.byUnit[params.privateAreaId]?.loading
    : false;
  const contractHistoryLoadingMore = params.privateAreaId
    ? !!contractHistorySlice.byUnit[params.privateAreaId]?.loadingMore
    : false;
  const handleLoadMoreContractHistory = useCallback(() => {
    const sid = params.privateAreaId;
    if (!sid) return;
    dispatch(fetchMoreContractHistoryByUnit({ unit_id: sid }) as any);
  }, [dispatch, params.privateAreaId]);

  const view = useMemo(() => {
    const r = current;
    const contractsSorted = [...(contractHistoryByUnit ?? [])].sort(
      (a: any, b: any) => {
        const ad = new Date(
          a?.created_at || a?.contract_start_date || 0,
        ).getTime();
        const bd = new Date(
          b?.created_at || b?.contract_start_date || 0,
        ).getTime();
        return ad - bd;
      },
    );
    // 唯一の真実は activeContract (Contracts テーブル) のみ。
    // legacy unit.contract / contractor_details / individual_details は読まない。
    const c = activeContract;
    const contractorType = String(c?.contractor_type || "");

    // 個人姓名は contractor_name を空白で分割 (新スキーマでは姓名連結)。
    const splitName = (full: string): { last: string; first: string } => {
      const t = (full || "").trim();
      if (!t) return { last: "", first: "" };
      const idx = t.indexOf(" ");
      if (idx < 0) return { last: t, first: "" };
      return { last: t.slice(0, idx), first: t.slice(idx + 1) };
    };
    const indi =
      c && c.contractor_type === "individual"
        ? splitName(c.contractor_name)
        : { last: "", first: "" };
    const repr =
      c && c.contractor_type === "corporate"
        ? splitName(c.contact_person || "")
        : { last: "", first: "" };

    const tenantName = c?.contractor_name || "";

    const formatCurrency = (v?: string | number) => {
      const num = Number(v || 0);
      return `¥${num.toLocaleString()}`;
    };

    // 退去状態は activeContract.status のみで判定。
    const isVacant = !c || c.status === "ended";

    return {
      propertyName: state.propertyName || "",
      // 別 id の current が一瞬残るのを防ぐため、url の id と
      // current.property_section_id が一致する時だけ landmark_name を使う
      privateAreaName:
        state.privateAreaName ||
        (r?.property_section_id === params.privateAreaId
          ? String(r?.landmark_name || "")
          : ""),
      address: state.address || "",
      remarks: isVacant ? "" : String(c?.contract_remarks || ""),
      contractorType: isVacant ? "" : contractorType,
      tenantName: isVacant ? "" : tenantName,
      tenantLastName:
        isVacant || c?.contractor_type !== "individual" ? "" : indi.last,
      tenantFirstName:
        isVacant || c?.contractor_type !== "individual" ? "" : indi.first,
      tenantPhone:
        isVacant || c?.contractor_type !== "individual" ? "" : c?.phone || "",
      tenantEmail:
        isVacant || c?.contractor_type !== "individual" ? "" : c?.email || "",
      companyName:
        isVacant || c?.contractor_type !== "corporate"
          ? ""
          : c?.company_name || c?.contractor_name || "",
      representativeLastName:
        isVacant || c?.contractor_type !== "corporate" ? "" : repr.last,
      representativeFirstName:
        isVacant || c?.contractor_type !== "corporate" ? "" : repr.first,
      companyPhone:
        isVacant || c?.contractor_type !== "corporate" ? "" : c?.phone || "",
      companyEmail:
        isVacant || c?.contractor_type !== "corporate" ? "" : c?.email || "",
      startDate: isVacant ? "" : c?.start_date || "",
      endDate: isVacant ? "" : c?.end_date || "",
      monthlyRent: isVacant ? "" : (c?.monthly_rent ?? ""),
      pricingType: isVacant ? "fixed" : (c?.pricing_type ?? "fixed"),
      unitPrice: isVacant ? "" : (c?.unit_price ?? ""),
      quantity: isVacant ? "" : (c?.quantity ?? ""),
      quantityUnit: isVacant ? "" : (c?.quantity_unit ?? ""),
      rentUnitPrice: isVacant ? "" : (c?.rent_unit_price ?? ""),
      deposit: isVacant ? "" : (c?.security_deposit ?? ""),
      keyMoney: isVacant ? "" : (c?.key_money ?? ""),
      contract_form: isVacant ? "" : c?.contract_form || "",
      maintenanceFee: isVacant ? "" : (c?.maintenance_fee ?? ""),
      maintenanceFeeUnitPrice: isVacant
        ? ""
        : (c?.maintenance_fee_unit_price ?? ""),
      rentStartDate: isVacant ? "" : c?.rent_start_date || "",
      cancellationDate: isVacant ? "" : c?.cancellation_date || "",
      depositAfter: isVacant ? "" : (c?.security_deposit_after ?? ""),
      otherFees: isVacant ? [] : c?.other_fees || [],
      status: c?.status === "active" ? ("active" as const) : ("ended" as const),
      // 添付書類は Unit.documents (UnitDocument[]) のみから数える。
      documentsCount: Array.isArray((activeContract as any)?.documents)
        ? (activeContract as any).documents.length
        : 0,
      history: (contractsSorted || [])
        .map((c: any, idx: number) => {
          const id = `${idx}-${c.created_at || c.contract_start_date || ""}`;
          // 作成日時を優先（チップ横の日時用）
          const rawDate = c.created_at || c.contract_start_date || "";
          // 最終更新日時（「その他」セクション用）
          const rawLastUpdatedAt = c.last_updated_at || "";
          const kind =
            c.update_type === "renewal"
              ? "renewal"
              : c.update_type === "tenant_change"
                ? "tenant_change"
                : c.update_type === "rent_change"
                  ? "rent_change"
                  : c.update_type === "document_upload"
                    ? "document_upload"
                    : c.update_type === "document_delete"
                      ? "document_delete"
                      : c.update_type === "contract_end"
                        ? "contract_end"
                        : "created";

          // 前の契約情報を取得（書類更新・削除は除外）
          let prev: any = null;
          for (let i = idx - 1; i >= 0; i--) {
            const candidate = contractsSorted[i];
            if (
              candidate.update_type !== "document_upload" &&
              candidate.update_type !== "document_delete"
            ) {
              prev = candidate;
              break;
            }
          }

          const changes: Array<{
            field: string;
            oldValue: string;
            newValue: string;
            corrected?: boolean;
            revisionHistory?: string;
          }> = [];

          // revision_historyから修正情報を取得
          const hasRevisionHistory =
            Array.isArray(c.revision_history) && c.revision_history.length > 0;

          if (prev) {
            // 契約開始日
            if (
              (prev.contract_start_date || "") !== (c.contract_start_date || "")
            ) {
              const revisionInfo = hasRevisionHistory
                ? c.revision_history
                    .filter(
                      (rev: any) =>
                        rev.changes && rev.changes.contract_start_date,
                    )
                    .map((rev: any) => {
                      const change = rev.changes.contract_start_date;
                      return `修正日時: ${new Date(rev.revised_at).toLocaleString("ja-JP")}\n修正前: ${change.before || ""}\n修正後: ${change.after || ""}\n修正理由: ${rev.reason || "なし"}`;
                    })
                    .join("\n\n---\n\n")
                : "";

              changes.push({
                field: "契約開始日",
                oldValue: String(prev.contract_start_date || ""),
                newValue: String(c.contract_start_date || ""),
                corrected: hasRevisionHistory && revisionInfo.length > 0,
                revisionHistory: revisionInfo,
              });
            }
            // 契約終了日
            if (
              (prev.contract_end_date || "") !== (c.contract_end_date || "")
            ) {
              // revision_historyから契約終了日の修正履歴を取得
              const revisionInfo = hasRevisionHistory
                ? c.revision_history
                    .filter(
                      (rev: any) =>
                        rev.changes && rev.changes.contract_end_date,
                    )
                    .map((rev: any) => {
                      const change = rev.changes.contract_end_date;
                      return `修正日時: ${new Date(rev.revised_at).toLocaleString("ja-JP")}\n修正前: ${change.before || ""}\n修正後: ${change.after || ""}\n修正理由: ${rev.reason || "なし"}`;
                    })
                    .join("\n\n---\n\n")
                : "";

              changes.push({
                field: "契約終了日",
                oldValue: String(prev.contract_end_date || ""),
                newValue: String(c.contract_end_date || ""),
                corrected: hasRevisionHistory && revisionInfo.length > 0,
                revisionHistory: revisionInfo,
              });
            }
            // 月額賃料（税別）
            if ((prev.monthly_rent || "") !== (c.monthly_rent || "")) {
              // revision_historyから月額賃料（税別）の修正履歴を取得
              const revisionInfo = hasRevisionHistory
                ? c.revision_history
                    .filter(
                      (rev: any) => rev.changes && rev.changes.monthly_rent,
                    )
                    .map((rev: any) => {
                      const change = rev.changes.monthly_rent;
                      return `修正日時: ${new Date(rev.revised_at).toLocaleString("ja-JP")}\n修正前: ${change.before || ""}\n修正後: ${change.after || ""}\n修正理由: ${rev.reason || "なし"}`;
                    })
                    .join("\n\n---\n\n")
                : "";

              changes.push({
                field: "月額賃料（税別）",
                oldValue: formatCurrency(prev.monthly_rent),
                newValue: formatCurrency(c.monthly_rent),
                corrected: hasRevisionHistory && revisionInfo.length > 0,
                revisionHistory: revisionInfo,
              });
            }
            // 敷金
            if ((prev.security_deposit || "") !== (c.security_deposit || "")) {
              const revisionInfo = hasRevisionHistory
                ? c.revision_history
                    .filter(
                      (rev: any) => rev.changes && rev.changes.security_deposit,
                    )
                    .map((rev: any) => {
                      const change = rev.changes.security_deposit;
                      return `修正日時: ${new Date(rev.revised_at).toLocaleString("ja-JP")}\n修正前: ${change.before || ""}\n修正後: ${change.after || ""}\n修正理由: ${rev.reason || "なし"}`;
                    })
                    .join("\n\n---\n\n")
                : "";

              changes.push({
                field: "敷金",
                oldValue: formatCurrency(prev.security_deposit),
                newValue: formatCurrency(c.security_deposit),
                corrected: hasRevisionHistory && revisionInfo.length > 0,
                revisionHistory: revisionInfo,
              });
            }
            // 礼金
            if ((prev.key_money || "") !== (c.key_money || "")) {
              const revisionInfo = hasRevisionHistory
                ? c.revision_history
                    .filter((rev: any) => rev.changes && rev.changes.key_money)
                    .map((rev: any) => {
                      const change = rev.changes.key_money;
                      return `修正日時: ${new Date(rev.revised_at).toLocaleString("ja-JP")}\n修正前: ${change.before || ""}\n修正後: ${change.after || ""}\n修正理由: ${rev.reason || "なし"}`;
                    })
                    .join("\n\n---\n\n")
                : "";

              changes.push({
                field: "礼金",
                oldValue: formatCurrency(prev.key_money),
                newValue: formatCurrency(c.key_money),
                corrected: hasRevisionHistory && revisionInfo.length > 0,
                revisionHistory: revisionInfo,
              });
            }
            // 契約形態
            if ((prev.contract_form || "") !== (c.contract_form || "")) {
              changes.push({
                field: "契約形態",
                oldValue: String(prev.contract_form || ""),
                newValue: String(c.contract_form || ""),
              });
            }
            // 共益費
            if ((prev.maintenance_fee || "") !== (c.maintenance_fee || "")) {
              changes.push({
                field: "共益費",
                oldValue: formatCurrency(prev.maintenance_fee),
                newValue: formatCurrency(c.maintenance_fee),
              });
            }
            // 賃料起算日
            if ((prev.rent_start_date || "") !== (c.rent_start_date || "")) {
              changes.push({
                field: "賃料起算日",
                oldValue: String(prev.rent_start_date || ""),
                newValue: String(c.rent_start_date || ""),
              });
            }
            // 解約予定日
            if (
              (prev.cancellation_date || "") !== (c.cancellation_date || "")
            ) {
              changes.push({
                field: "解約予定日",
                oldValue: String(prev.cancellation_date || ""),
                newValue: String(c.cancellation_date || ""),
              });
            }
            // 償却後敷金
            if (
              String((prev as any).security_deposit_after || "") !==
              String((c as any).security_deposit_after || "")
            ) {
              changes.push({
                field: "償却後敷金",
                oldValue: (prev as any).security_deposit_after
                  ? `¥${Number((prev as any).security_deposit_after).toLocaleString()}`
                  : "",
                newValue: (c as any).security_deposit_after
                  ? `¥${Number((c as any).security_deposit_after).toLocaleString()}`
                  : "",
              });
            }
          }

          // テナント情報の差分は全 update_type で検知する
          // (renewal/rent_change で同時に変更された場合も履歴に出すため)
          if (prev) {
            const prevTenantName = prev?.tenant_name || "";
            const currTenantName = c.tenant_name || "";
            const prevTenantPhone = prev?.tenant_phone || "";
            const currTenantPhone = c.tenant_phone || "";
            const prevTenantEmail = prev?.tenant_email || "";
            const currTenantEmail = c.tenant_email || "";

            if (prevTenantName !== currTenantName) {
              const revisionInfo = hasRevisionHistory
                ? c.revision_history
                    .filter(
                      (rev: any) => rev.changes && rev.changes.tenant_name,
                    )
                    .map((rev: any) => {
                      const change = rev.changes.tenant_name;
                      return `修正日時: ${new Date(rev.revised_at).toLocaleString("ja-JP")}\n修正前: ${change.before || ""}\n修正後: ${change.after || ""}\n修正理由: ${rev.reason || "なし"}`;
                    })
                    .join("\n\n---\n\n")
                : "";

              changes.push({
                field: c.contractor_type === "corporate" ? "会社名" : "氏名",
                oldValue: prevTenantName,
                newValue: currTenantName,
                corrected: hasRevisionHistory && revisionInfo.length > 0,
                revisionHistory: revisionInfo,
              });
            }
            if (prevTenantPhone !== currTenantPhone) {
              const revisionInfo = hasRevisionHistory
                ? c.revision_history
                    .filter(
                      (rev: any) => rev.changes && rev.changes.tenant_phone,
                    )
                    .map((rev: any) => {
                      const change = rev.changes.tenant_phone;
                      return `修正日時: ${new Date(rev.revised_at).toLocaleString("ja-JP")}\n修正前: ${change.before || ""}\n修正後: ${change.after || ""}\n修正理由: ${rev.reason || "なし"}`;
                    })
                    .join("\n\n---\n\n")
                : "";

              changes.push({
                field: "電話番号",
                oldValue: prevTenantPhone,
                newValue: currTenantPhone,
                corrected: hasRevisionHistory && revisionInfo.length > 0,
                revisionHistory: revisionInfo,
              });
            }
            if (prevTenantEmail !== currTenantEmail) {
              const revisionInfo = hasRevisionHistory
                ? c.revision_history
                    .filter(
                      (rev: any) => rev.changes && rev.changes.tenant_email,
                    )
                    .map((rev: any) => {
                      const change = rev.changes.tenant_email;
                      return `修正日時: ${new Date(rev.revised_at).toLocaleString("ja-JP")}\n修正前: ${change.before || ""}\n修正後: ${change.after || ""}\n修正理由: ${rev.reason || "なし"}`;
                    })
                    .join("\n\n---\n\n")
                : "";

              changes.push({
                field: "メールアドレス",
                oldValue: prevTenantEmail,
                newValue: currTenantEmail,
                corrected: hasRevisionHistory && revisionInfo.length > 0,
                revisionHistory: revisionInfo,
              });
            }

            if (c.contractor_type === "corporate") {
              const prevRepName = prev?.representative_name || "";
              const currRepName = c.representative_name || "";
              if (prevRepName !== currRepName) {
                const revisionInfo = hasRevisionHistory
                  ? c.revision_history
                      .filter(
                        (rev: any) =>
                          rev.changes && rev.changes.representative_name,
                      )
                      .map((rev: any) => {
                        const change = rev.changes.representative_name;
                        return `修正日時: ${new Date(rev.revised_at).toLocaleString("ja-JP")}\n修正前: ${change.before || ""}\n修正後: ${change.after || ""}\n修正理由: ${rev.reason || "なし"}`;
                      })
                      .join("\n\n---\n\n")
                  : "";

                changes.push({
                  field: "代表者名",
                  oldValue: prevRepName,
                  newValue: currRepName,
                  corrected: hasRevisionHistory && revisionInfo.length > 0,
                  revisionHistory: revisionInfo,
                });
              }
            }
          }

          // 備考（常に最後）
          if (
            prev &&
            (prev.contract_remarks || "") !== (c.contract_remarks || "") &&
            c.contract_remarks
          ) {
            changes.push({
              field: "備考",
              oldValue: String(prev.contract_remarks || ""),
              newValue: String(c.contract_remarks || ""),
            });
          }

          let contractDetails:
            | {
                tenantName?: string;
                tenantPhone?: string;
                tenantEmail?: string;
                startDate?: string;
                endDate?: string;
                monthlyRent?: number;
                deposit?: number;
                keyMoney?: number;
                remarks?: string;
                contractForm?: string;
                maintenanceFee?: number;
                pricingType?: "fixed" | "unit_quantity";
                unitPrice?: number;
                quantity?: number;
                quantityUnit?: string;
                rentStartDate?: string;
                cancellationDate?: string;
                depositAfter?: number;
                otherFees?: Array<{ label: string; amount: number }>;
              }
            | undefined = undefined;

          // ContractHistory レコードは tenant_name / tenant_phone / tenant_email 等の
          // スナップショットを必ず含む前提 (BE 側で保証)。
          // 退去 (contract_end) も「前契約者情報を見る」モーダルで表示するためスナップショットを入れる。
          if (
            c.update_type === "created" ||
            c.update_type === "tenant_change" ||
            c.update_type === "renewal" ||
            c.update_type === "rent_change" ||
            c.update_type === "contract_end"
          ) {
            contractDetails = {
              tenantName: c.tenant_name || "",
              tenantPhone: c.tenant_phone || "",
              tenantEmail: c.tenant_email || "",
              startDate: c.contract_start_date || "",
              endDate: c.contract_end_date || "",
              monthlyRent: Number(c.monthly_rent || 0),
              deposit: Number(c.security_deposit || 0),
              keyMoney: Number(c.key_money || 0),
              remarks: c.contract_remarks || "",
              contractForm: c.contract_form,
              maintenanceFee: Number(c.maintenance_fee || 0),
              pricingType: c.pricing_type,
              unitPrice:
                c.unit_price === undefined
                  ? undefined
                  : Number(c.unit_price),
              quantity:
                c.quantity === undefined ? undefined : Number(c.quantity),
              quantityUnit: c.quantity_unit,
              rentStartDate: c.rent_start_date || "",
              cancellationDate: c.cancellation_date,
              depositAfter: Number((c as any)?.security_deposit_after || 0),
              otherFees: (c as any)?.other_fees,
              contractorType: (c as any)?.contractor_type,
            } as any;
          }

          // 編集済みかどうかを判定
          const isEdited =
            c.corrected ||
            (Array.isArray(c.revision_history) &&
              c.revision_history.length > 0);

          return {
            id,
            date: formatDisplayDateTime(rawDate), // 作成日時（チップ横）
            type: kind as any,
            description:
              c.update_type === "renewal"
                ? "契約更新"
                : c.update_type === "tenant_change"
                  ? "契約者更新"
                  : c.update_type === "rent_change"
                    ? "賃料更新"
                    : c.update_type === "document_upload"
                      ? "書類追加"
                      : c.update_type === "document_delete"
                        ? "書類削除"
                        : c.update_type === "contract_end"
                          ? "契約終了"
                          : "新規契約",
            note: c.contract_remarks,
            changes: changes.length > 0 ? changes : undefined,
            contractDetails,
            corrected: c.corrected || false,
            revisionHistory: c.revision_history || [],
            createdBy: c.created_by_name || "",
            updatedBy: c.updated_by_name || "",
            lastUpdatedAt: formatDisplayDateTime(rawLastUpdatedAt), // 最終更新日時（「その他」セクション）
            isEdited, // 編集済みフラグを追加
            // 書類更新用のフィールド
            tenantName: (c as any).tenant_name || "",
            uploadedDocuments: (c as any).uploaded_documents || [],
            // 書類削除用のフィールド
            deletedDocuments: (c as any).deleted_documents || [],
            // 退去処理用のフィールド
            departureDate: (c as any).departure_date || "",
            departureReason: (c as any).departure_reason || "",
          };
        })
        .reverse(),
      // 占有状態は activeContract に基づき判定 (active 契約があれば occupied)。
      // PropertySection / Unit の occupancy_status は refetch タイミングのズレで
      // ステイルになり得るので使わない。
      occupancyStatus: c && c.status === "active" ? "occupied" : "vacant",
      // 最終退去日（最新の契約終了イベントから取得）
      lastDepartureDate: (() => {
        const contractEndEvent = [...contractsSorted]
          .reverse()
          .find((c: any) => c.update_type === "contract_end");
        return (
          contractEndEvent?.departure_date ||
          contractEndEvent?.contract_end_date ||
          ""
        );
      })(),
    };
  }, [
    current,
    state,
    contractHistoryByUnit,
    activeContract,
    params.privateAreaId,
  ]);

  // URL の物件/区画 ID が不正な場合は初期取得エラーとして表示する。
  const fetchDetail = useCallback(async () => {
    if (!embedded && isStrictlyInvalidPrivateAreaTabQuery(searchParams)) {
      return;
    }

    const propertyId = params.propertyId;
    const sid = params.privateAreaId;
    setInitialLoadError(null);

    if (!propertyId || propertyId === "undefined") {
      setInitialLoadError("notFound");
      return;
    }
    if (!sid || sid === "undefined") {
      setInitialLoadError("notFound");
      return;
    }

    const [propertyResult, sectionResult] = await Promise.all([
      dispatch(fetchPropertyDetail(propertyId)),
      dispatch(fetchPropertySectionById({ property_section_id: sid })),
    ]);

    if (fetchPropertyDetail.rejected.match(propertyResult)) {
      const payload = propertyResult.payload as ApiErrorPayload | undefined;
      setInitialLoadError(payload?.errorType ?? "unknown");
      return;
    }

    const section = sectionResult.payload as
      | { property_id?: string; property_section_id?: string }
      | undefined;
    if (fetchPropertySectionById.rejected.match(sectionResult)) {
      const payload = sectionResult.payload as ApiErrorPayload | undefined;
      setInitialLoadError(payload?.errorType ?? "unknown");
      return;
    }

    if (
      !section?.property_section_id ||
      String(section.property_id) !== String(propertyId)
    ) {
      setInitialLoadError("notFound");
    }
  }, [
    dispatch,
    embedded,
    params.privateAreaId,
    params.propertyId,
    searchParams,
  ]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // AI チャットアクション完了時 (refetchBus.emit) に区画 / 契約データを再取得。
  // data-entity="unit" はこのページに常時存在し、data-entity="contract" は
  // テナントタブ表示時に存在する。どちらの emit でも区画(section)/Unit/契約一覧を
  // まとめて取り直す (契約は ContractorTab ではなく親で取得しているため)。
  const refetchUnitPage = useCallback(async () => {
    const sid = params.privateAreaId;
    if (!sid || sid === "undefined") return;
    await fetchDetail();
    dispatch(fetchUnitById({ unit_id: sid }) as any);
    dispatch(fetchContractsByUnit({ unit_id: sid, status: "active" }) as any);
  }, [params.privateAreaId, fetchDetail, dispatch]);
  useRefetchOn("unit", params.privateAreaId, refetchUnitPage);
  useRefetchOn(
    "contract",
    (current as any)?.id || (current as any)?.contract_id || undefined,
    refetchUnitPage,
  );

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  // 物件詳細ページ (`pages/properties/Detail`) と同様、部屋詳細ページでも
  //「タブ枠は固定・中身だけ内部スクロール」を成立させるため、body / html の
  // スクロールを封じる。離脱時に元の overflow を復元する。
  // 埋め込み (PreviewDrawer 等) で表示する場合は親側のスクロールを尊重する
  // ため body lock しない。
  useEffect(() => {
    if (embedded) return;
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, [embedded]);

  // 物件名と専有部名を取得（最新データを優先）
  const propertyName =
    propertyData?.name || state.propertyName || view.propertyName || "";
  // currentが今のprivateAreaIdと一致する場合のみReduxデータを使う
  const isCurrentMatchingRoute =
    current?.property_section_id === params.privateAreaId;
  const privateAreaName =
    (isCurrentMatchingRoute && current?.landmark_name) ||
    state.privateAreaName ||
    view.privateAreaName ||
    "";

  // パンくずに propertyName と privateAreaName を渡す
  const hasUpdatedNavigation = useRef(false);
  useEffect(() => {
    const needsUpdate =
      (propertyName && propertyName !== location?.state?.propertyName) ||
      (privateAreaName && privateAreaName !== location?.state?.privateAreaName);

    if (needsUpdate && !hasUpdatedNavigation.current) {
      hasUpdatedNavigation.current = true;
      const cleanState = {
        ...(location?.state || {}),
        ...(propertyName && propertyName !== location?.state?.propertyName
          ? { propertyName, propName: propertyName }
          : {}),
        ...(privateAreaName &&
        privateAreaName !== location?.state?.privateAreaName
          ? { privateAreaName }
          : {}),
      };
      navigate(`${location.pathname}${location.search}`, {
        state: cleanState,
        replace: true,
      });
      // ナビゲーション後にフラグをリセット
      setTimeout(() => {
        hasUpdatedNavigation.current = false;
      }, 100);
    }
  }, [
    propertyName,
    privateAreaName,
    location?.state?.propertyName,
    location?.state?.privateAreaName,
    location.pathname,
    navigate,
  ]);

  // const handleBackToProperty = () => {
  //   navigate(`/properties/${params.propertyId}`);
  // };

  // 区画タブ用フォーム
  const isAncillary = resolveIsAncillaryUnit(
    unit?.unit_id === params.privateAreaId ? unit?.revenue_type : undefined,
    current?.revenue_type,
  );

  // タブ設定（テナント管理を親タブ化、予算はコメントアウトで残置）
  // - section : 区画詳細
  // - tenant  : テナント管理（サブタブ: 契約情報 / 入金管理）
  // - repair  : 工事履歴
  // - contract: 契約履歴
  // - budget  : 予算（一時コメントアウト）
  // 収益あり (is_revenue_generating=true / Unit.revenue_type が stock_rent・stock_other 由来) なら
  //   4 タブ全て、収益なしは「区画詳細 / 工事履歴」のみ。
  const isRevenueUnit = !!current?.is_revenue_generating;
  const hasPrivateAreaData = Boolean(current?.property_section_id);
  const hasStrictlyInvalidTabQuery =
    !embedded && isStrictlyInvalidPrivateAreaTabQuery(searchParams);
  const hasValidTabQuery =
    embedded || isValidPrivateAreaTabQuery(searchParams, isRevenueUnit);

  const TAB_CONFIG = getPrivateAreaTabConfig(isRevenueUnit);
  const TAB_IDS = getPrivateAreaTabKeys(isRevenueUnit);

  const TENANT_SUB_TAB_CONFIG = PRIVATE_AREA_TENANT_SUBTABS.map(
    (tab, index) => ({
      id: PRIVATE_AREA_TENANT_SUBTAB_KEYS[index],
      label: tab.label,
    }),
  );
  const TENANT_SUB_TAB_IDS = PRIVATE_AREA_TENANT_SUBTAB_KEYS;

  const tabsForParsing = hasPrivateAreaData
    ? getPrivateAreaTabs(isRevenueUnit)
    : getPrivateAreaTabs(true);

  const initialTabIndex = embedded
    ? (overrideInitialTab ?? DEFAULT_PRIVATE_AREA_TAB_INDEX)
    : parseTabId(
        searchParams.get("tab"),
        tabsForParsing,
        DEFAULT_PRIVATE_AREA_TAB_INDEX,
      );
  const [activeTab, setActiveTab] = useState<PrivateAreaTabKey>(
    TAB_IDS[initialTabIndex] ?? TAB_IDS[0],
  );

  const initialSubTabIndex = embedded
    ? (overrideInitialSubTab ?? DEFAULT_PRIVATE_AREA_TENANT_SUBTAB_INDEX)
    : parseTabId(
        searchParams.get("subtab"),
        PRIVATE_AREA_TENANT_SUBTABS,
        DEFAULT_PRIVATE_AREA_TENANT_SUBTAB_INDEX,
      );
  const [activeTenantSubTab, setActiveTenantSubTab] =
    useState<PrivateAreaTenantSubTabKey>(
      TENANT_SUB_TAB_IDS[initialSubTabIndex] ?? TENANT_SUB_TAB_IDS[0],
    );
  const contractsLoading =
    activeTab === "tenant" &&
    activeTenantSubTab === "contract-info" &&
    Boolean(params.privateAreaId) &&
    (!contractsByUnitEntry || !!contractsByUnitEntry.loading);

  // ドロワー埋め込み時は URL を書き換えない代わりに、現在のタブを親へ通知する。
  // 全画面表示時に同じ query を組み立てるための状態として使う。
  useEffect(() => {
    if (!embedded || !onEmbeddedTabChange) return;

    const tab = TAB_IDS.indexOf(activeTab);
    const subtab =
      activeTab === "tenant"
        ? TENANT_SUB_TAB_IDS.indexOf(activeTenantSubTab)
        : undefined;
    onEmbeddedTabChange({ tab: Math.max(0, tab), subtab });
  }, [
    activeTab,
    activeTenantSubTab,
    embedded,
    onEmbeddedTabChange,
    TAB_IDS,
    TENANT_SUB_TAB_IDS,
  ]);

  useEffect(() => {
    const sid = params.privateAreaId;
    if (!sid || sid === "undefined" || activeTab === "section") return;
    dispatch(fetchUnitById({ unit_id: sid }));
  }, [dispatch, params.privateAreaId, activeTab]);

  // タブを開いた瞬間に該当タブで使う API を再フェッチする。
  // PaymentHistoryTab と同じ「タブが見せるデータはタブ自身が責任を持つ」パターンに倣い、
  // テナント情報サブタブは Contracts、契約履歴タブ (contract) は ContractHistory を fetch。
  useEffect(() => {
    const sid = params.privateAreaId;
    if (!sid) return;
    if (activeTab === "tenant" && activeTenantSubTab === "contract-info") {
      dispatch(fetchContractsByUnit({ unit_id: sid, status: "active" }));
    }
    if (activeTab === "contract") {
      dispatch(fetchContractHistoryByUnit({ unit_id: sid }));
    }
  }, [dispatch, params.privateAreaId, activeTab, activeTenantSubTab]);

  const handleTabChange = (nextIndex: number) => {
    setActiveTab(TAB_IDS[nextIndex]);
    if (embedded) return; // 埋め込み時は URL を書き換えない
    const params = new URLSearchParams(searchParams);
    params.set("tab", String(nextIndex));
    // 親タブを切り替えたタイミングで他タブのサブタブ index は不要なので除去
    if (TAB_IDS[nextIndex] !== "tenant") {
      params.delete("subtab");
    }
    // 旧キー (camelCase) が残っていたら常に掃除
    params.delete("subTab");
    const cleanState = location.state ? { ...location.state } : {};
    navigate(
      { search: `?${params.toString()}` },
      { replace: true, state: cleanState },
    );
  };

  const handleTenantSubTabChange = (nextIndex: number) => {
    setActiveTenantSubTab(TENANT_SUB_TAB_IDS[nextIndex]);
    if (embedded) return;
    const params = new URLSearchParams(searchParams);
    params.set("subtab", String(nextIndex));
    const cleanState = location.state ? { ...location.state } : {};
    navigate(
      { search: `?${params.toString()}` },
      { replace: true, state: cleanState },
    );
  };

  // URL の tab パラメータが外部から変わった時に state を追従 (埋め込み時はスキップ)
  useEffect(() => {
    if (embedded) return;
    if (hasStrictlyInvalidTabQuery) return;
    if (hasPrivateAreaData && !hasValidTabQuery) return;

    const tabIndex = parseTabId(
      searchParams.get("tab"),
      getPrivateAreaTabs(isRevenueUnit),
      DEFAULT_PRIVATE_AREA_TAB_INDEX,
    );
    const nextId = getPrivateAreaTabKeys(isRevenueUnit)[tabIndex];
    if (nextId && nextId !== activeTab) {
      setActiveTab(nextId);
    }

    const subIndex = parseTabId(
      searchParams.get("subtab"),
      PRIVATE_AREA_TENANT_SUBTABS,
      DEFAULT_PRIVATE_AREA_TENANT_SUBTAB_INDEX,
    );
    const nextSubId = TENANT_SUB_TAB_IDS[subIndex];
    if (nextSubId && nextSubId !== activeTenantSubTab) {
      setActiveTenantSubTab(nextSubId);
    }
  }, [
    activeTab,
    activeTenantSubTab,
    embedded,
    hasPrivateAreaData,
    hasStrictlyInvalidTabQuery,
    hasValidTabQuery,
    isRevenueUnit,
    searchParams,
  ]);

  // 契約者インライン編集フォーム
  const contractorForm = useForm<any>({
    defaultValues: {
      tenantLastName: "",
      tenantFirstName: "",
      tenantPhone: "",
      tenantEmail: "",
      companyName: "",
      representativeLastName: "",
      representativeFirstName: "",
      companyPhone: "",
      companyEmail: "",
      contract_form: "",
      startDate: "",
      endDate: "",
      cancellation_date: "",
      note: "",
      documents: {},
      // 既存書類のうち削除予約された URL リスト。X クリックで追加・再クリックで戻す。
      // 「保存」押下時に一括で deletePropertySectionDocument 発行。
      documents_to_delete: [],
    },
  });

  // レントロールインライン編集フォーム
  const rentrollForm = useForm<any>({
    defaultValues: {
      monthlyRent: "",
      pricingType: "fixed",
      unitPrice: "",
      quantity: "1",
      quantityUnit: "台",
      rentUnitPrice: "",
      keyMoney: "",
      maintenance_fee: "",
      maintenanceFeeUnitPrice: "",
      total_rent: "",
      security_deposit_before: "",
      security_deposit_after: "",
      initial_contract_start_date: "",
      rent_start_date: "",
    },
  });

  // 基本情報タブ (SectionTabForm = 区画情報 + 区画画像) の未保存状態。
  const [sectionDirty, setSectionDirty] = useState(false);

  // PreviewDrawer 内で開かれている場合、閉じ時の破棄確認用に dirty を報告 (外では no-op)。
  // 基本情報タブ / 契約者情報 / レントロール条件のインライン編集フォームの未保存を拾う。
  usePreviewDrawerUnsaved(
    sectionDirty ||
      contractorForm.formState.isDirty ||
      rentrollForm.formState.isDirty,
  );

  // その他費用の state
  const [otherFees, setOtherFees] = useState<
    Array<{ label: string; amount: string }>
  >([]);

  // activeContract の値で contractorForm を充填する。
  const populateContractorForm = () => {
    const c = activeContract;
    contractorForm.reset(
      {
        tenantLastName: view.tenantLastName || "",
        tenantFirstName: view.tenantFirstName || "",
        tenantPhone: view.tenantPhone || "",
        tenantEmail: view.tenantEmail || "",
        companyName: view.companyName || "",
        representativeLastName: view.representativeLastName || "",
        representativeFirstName: view.representativeFirstName || "",
        companyPhone: view.companyPhone || "",
        companyEmail: view.companyEmail || "",
        contract_form: c?.contract_form || "",
        startDate: c?.start_date || "",
        endDate: c?.end_date || "",
        cancellation_date: c?.cancellation_date || "",
        note: c?.contract_remarks || "",
        documents: {},
        documents_to_delete: [],
      },
      { keepDirty: false, keepDirtyValues: false },
    );
  };

  // activeContract から賃料/敷金フォームを充填する。legacy contractor_details は読まない。
  const populateRentrollForm = () => {
    const c = activeContract;
    const s = (v: any) => (v == null || v === "" ? "" : String(v));
    rentrollForm.reset(
      {
        monthlyRent: s(view.monthlyRent),
        pricingType: view.pricingType || "fixed",
        unitPrice: s(view.unitPrice),
        quantity: s(view.quantity || 1),
        quantityUnit: view.quantityUnit || "台",
        rentUnitPrice: s(view.rentUnitPrice),
        keyMoney: s(view.keyMoney),
        maintenance_fee: s(view.maintenanceFee),
        maintenanceFeeUnitPrice: s(view.maintenanceFeeUnitPrice),
        total_rent: s((c as any)?.total_rent),
        security_deposit_before: s(c?.security_deposit),
        security_deposit_after: s(c?.security_deposit_after),
        initial_contract_start_date: c?.start_date ?? "",
        rent_start_date: c?.rent_start_date ?? "",
      },
      { keepDirty: false, keepDirtyValues: false },
    );
    const existingFees = Array.isArray(c?.other_fees) ? c.other_fees : [];
    setOtherFees(
      existingFees.map((f: any) => ({
        label: f.label || "",
        amount: f.amount != null ? String(f.amount) : "",
      })),
    );
  };

  const handleContractorInlineSubmit = async (
    data: any,
    // 注意: react-hook-form の handleSubmit は (data, event) でこの関数を呼ぶため、
    // フォーム送信時は第2引数に Event が入る。確認スキップは確定ボタンからの
    // 明示的な true のときだけ有効にする (Event は truthy なので === true で判定)。
    skipLinkedConfirmArg: unknown = false,
  ) => {
    const skipLinkedConfirm = skipLinkedConfirmArg === true;
    const sid = params.privateAreaId;
    if (!sid) return;

    const contractorType = activeContract?.contractor_type || "individual";
    const targetContractId = activeContract?.contract_id ?? null;

    const documentPayloads = await encodeSectionDocumentPayloads(
      data?.documents as Record<string, File[] | undefined> | undefined,
    );
    const documentsToDelete: string[] = Array.isArray(data?.documents_to_delete)
      ? data.documents_to_delete
      : [];

    const dirtyFields =
      (contractorForm.formState.dirtyFields as Record<string, unknown>) || {};
    const hasNonDocumentDirty = Object.keys(dirtyFields).some(
      (k) => k !== "documents" && k !== "documents_to_delete",
    );

    if (
      !hasNonDocumentDirty &&
      documentPayloads.length === 0 &&
      documentsToDelete.length === 0
    ) {
      showToast({ type: "info", message: "変更がありません" });
      return;
    }

    if (hasNonDocumentDirty && !targetContractId) {
      showToast({
        type: "error",
        message: "編集対象の契約が見つかりません",
      });
      return;
    }

    // 新 Contracts テーブル (PATCH /api/v1/contracts/:contract_id) 用 body に変換
    const isCorporate = contractorType === "corporate";
    const contractorName = isCorporate
      ? data.companyName || ""
      : [data.tenantLastName, data.tenantFirstName].filter(Boolean).join(" ");
    const phone = isCorporate ? data.companyPhone : data.tenantPhone;
    const email = isCorporate ? data.companyEmail : data.tenantEmail;
    const contactPerson = isCorporate
      ? [data.representativeLastName, data.representativeFirstName]
          .filter(Boolean)
          .join(" ")
      : undefined;

    // 契約者の識別情報 (氏名・法人名・代表者・連絡先) は顧客マスタが正本。
    // 紐づく customer_id があれば、これらの編集は updateCustomer 経由にして
    // BE 側で全アクティブ契約へ波及＋tenant_change 履歴を残す。契約固有項目
    // (契約形態・期間・備考・書類) は従来どおり updateContract で当該契約のみ更新する。
    const customerId = (activeContract as any)?.customer_id as
      | string
      | undefined;
    const IDENTITY_FIELDS = [
      "tenantLastName",
      "tenantFirstName",
      "tenantPhone",
      "tenantEmail",
      "companyName",
      "representativeLastName",
      "representativeFirstName",
      "companyPhone",
      "companyEmail",
    ];
    const CONTRACT_FIELDS = [
      "contract_form",
      "cancellation_date",
      "note",
      "startDate",
      "endDate",
    ];
    const identityDirty = IDENTITY_FIELDS.some((k) => dirtyFields[k]);
    const contractSpecificDirty = CONTRACT_FIELDS.some((k) => dirtyFields[k]);
    const routeIdentityToCustomer = !!customerId && identityDirty;

    // 識別情報が変わり、かつ同じ契約者が他のアクティブ契約にも紐づく場合は、
    // 波及することを確認するモーダルを出す (確定で skipLinkedConfirm=true で再実行)。
    if (!skipLinkedConfirm && routeIdentityToCustomer) {
      setLinkedConfirmLoading(true);
      try {
        const res = await dispatch(
          searchActiveContractsByCustomer({ customer_id: customerId! }) as any,
        );
        const linked = searchActiveContractsByCustomer.fulfilled.match(res)
          ? (res.payload as Array<any>)
          : [];
        const others = linked
          .filter((c) => c.contract_id !== targetContractId)
          .map((c) => ({
            contract_id: c.contract_id,
            property_name: c.property_name || "",
            area_type: c.unit_area_type || undefined,
            unit_name: c.unit_name || "",
          }));
        if (others.length > 0) {
          setLinkedContracts(others);
          setPendingLinkedAction({
            run: () => handleContractorInlineSubmit(data, true),
          });
          setLinkedConfirmOpen(true);
          return; // 確認待ち。確定後に本処理を skipLinkedConfirm=true で再実行する。
        }
      } finally {
        setLinkedConfirmLoading(false);
      }
    }

    // routeIdentityToCustomer のときは contract body から識別情報を除き、
    // 当該契約への二重 tenant_change 履歴を防ぐ。
    const contractBody: any = {
      ...(routeIdentityToCustomer
        ? {}
        : {
            contractor_type: contractorType || "individual",
            contractor_name: contractorName,
            ...(isCorporate && { company_name: data.companyName || "" }),
            ...(isCorporate &&
              contactPerson && { contact_person: contactPerson }),
            ...(phone !== undefined && { phone: phone || "" }),
            ...(email !== undefined && { email: email || "" }),
          }),
      contract_form: data.contract_form || "",
      cancellation_date: data.cancellation_date || "",
      contract_remarks: data.note || "",
      start_date: data.startDate ?? "",
      end_date: data.endDate ?? "",
    };

    // 識別情報 → 顧客マスタ更新 (BE が全アクティブ契約へ波及 + 履歴追記)
    if (routeIdentityToCustomer) {
      const customerBody = isCorporate
        ? {
            customer_type: "corporate" as const,
            company_name: data.companyName || "",
            representative_last_name: data.representativeLastName || "",
            representative_first_name: data.representativeFirstName || "",
            phone: data.companyPhone || "",
            email: data.companyEmail || "",
          }
        : {
            customer_type: "individual" as const,
            last_name: data.tenantLastName || "",
            first_name: data.tenantFirstName || "",
            phone: data.tenantPhone || "",
            email: data.tenantEmail || "",
          };
      const custRes = await dispatch(
        updateCustomer({ customer_id: customerId!, body: customerBody }) as any,
      );
      if (!updateCustomer.fulfilled.match(custRes)) {
        showToast({ type: "error", message: "契約者情報の更新に失敗しました" });
        return;
      }
    }

    // 契約固有項目の更新: 識別情報を顧客マスタに寄せた場合は契約固有が dirty の
    // ときだけ、寄せない場合は従来どおり非書類変更があれば updateContract を呼ぶ。
    const shouldUpdateContract = routeIdentityToCustomer
      ? contractSpecificDirty
      : hasNonDocumentDirty;
    if (shouldUpdateContract) {
      const result = await dispatch(
        updateContract({
          contract_id: targetContractId!,
          body: contractBody,
        }) as any,
      );
      if (!updateContract.fulfilled.match(result)) {
        showToast({ type: "error", message: "契約者情報の更新に失敗しました" });
        return;
      }
    }

    // 書類は Contract レコードの documents 配列で管理する (Unit.documents とは独立)。
    // 削除予約 (documentsToDelete) と新規アップロード (documentPayloads) を一括で
    // updateContract に送る = (existing - deleted) + new。
    if (
      targetContractId &&
      (documentPayloads.length > 0 || documentsToDelete.length > 0)
    ) {
      const existing = Array.isArray((activeContract as any)?.documents)
        ? ((activeContract as any).documents as Array<{
            document_id: string;
            category: string;
            label?: string;
            file_name: string;
            s3_key: string;
            uploaded_at: string;
          }>)
        : [];
      const deleteSet = new Set(
        documentsToDelete.map((u) => extractS3KeyFromUrl(u) || u),
      );
      const kept = existing.filter(
        (d) =>
          !deleteSet.has(d.s3_key) &&
          !deleteSet.has(extractS3KeyFromUrl(d.s3_key) || d.s3_key),
      );
      const nowIso = new Date().toISOString();
      const added: Array<{
        document_id: string;
        category: string;
        file_name: string;
        s3_key: string;
        uploaded_at: string;
      }> = [];
      let counter = 0;
      for (const { category, files } of documentPayloads) {
        for (const f of files as any[]) {
          added.push({
            document_id: `${Date.now()}-${counter++}`,
            category,
            file_name: f?.originalFileName || `document-${counter}`,
            s3_key: f?.data || "",
            uploaded_at: nowIso,
          });
        }
      }
      const docResult = await dispatch(
        updateContract({
          contract_id: targetContractId,
          body: { documents: [...kept, ...added] } as any,
        }) as any,
      );
      if (!updateContract.fulfilled.match(docResult)) {
        const apiMessage =
          (docResult as any)?.payload?.message ||
          (docResult as any)?.payload?.data?.message;
        showToast({
          type: "error",
          message: apiMessage || "書類の更新に失敗しました",
        });
        return;
      }
    }

    // 保存後の再フェッチ: PropertySection (legacy 表示用) / Unit (current_contract_id) /
    // Contracts (activeContract) の 3 つを揃えて refetch しないと、
    // form の populate が古いデータで上書きされる。
    await Promise.all([
      dispatch(fetchPropertySectionById({ property_section_id: sid }) as any),
      dispatch(fetchUnitById({ unit_id: sid }) as any),
      dispatch(fetchContractsByUnit({ unit_id: sid, status: "active" }) as any),
    ]);
    const docTouched =
      documentPayloads.length > 0 || documentsToDelete.length > 0;
    showToast({
      type: "success",
      message: hasNonDocumentDirty
        ? docTouched
          ? "契約者情報と書類を更新しました"
          : "契約者情報を更新しました"
        : "書類を更新しました",
    });
    contractorForm.reset({
      ...contractorForm.getValues(),
      documents: {},
      documents_to_delete: [],
    });
  };

  // 波及確認モーダルの「変更する」: 保留アクション (確認スキップ版) を実行する。
  const handleLinkedConfirm = async () => {
    const action = pendingLinkedAction;
    setLinkedConfirmOpen(false);
    if (action) await action.run();
    setPendingLinkedAction(null);
    setLinkedContracts([]);
  };
  const handleLinkedCancel = () => {
    setLinkedConfirmOpen(false);
    setPendingLinkedAction(null);
    setLinkedContracts([]);
  };

  const handleRentrollInlineSubmit = async (data: any) => {
    const sid = params.privateAreaId;
    if (!sid) return;

    const targetContractId = activeContract?.contract_id;
    if (!targetContractId) {
      showToast({
        type: "error",
        message: "契約条件の更新対象が見つかりません",
      });
      return;
    }

    const toNum = (v: any): number | undefined => {
      if (v === "" || v == null) return undefined;
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };

    // 契約条件 (賃料 / 敷礼 / 共益費 / その他費用 / 契約期間) は Contracts テーブル側のフィールド。
    // dirty なフィールドのみ body に積む (undefined を送ると spread merge で上書きされる)。
    const dirty =
      (rentrollForm.formState.dirtyFields as Record<string, unknown>) || {};
    const body: Record<string, unknown> = {};
    if (dirty.monthlyRent) body.monthly_rent = toNum(data.monthlyRent);
    if (dirty.pricingType) body.pricing_type = data.pricingType;
    if (dirty.unitPrice) body.unit_price = toNum(data.unitPrice);
    if (dirty.quantity) body.quantity = toNum(data.quantity);
    if (dirty.quantityUnit) body.quantity_unit = data.quantityUnit;
    if (dirty.rentUnitPrice) body.rent_unit_price = toNum(data.rentUnitPrice);
    if (dirty.keyMoney) body.key_money = toNum(data.keyMoney);
    if (dirty.maintenance_fee)
      body.maintenance_fee = toNum(data.maintenance_fee);
    if (dirty.maintenanceFeeUnitPrice)
      body.maintenance_fee_unit_price = toNum(data.maintenanceFeeUnitPrice);
    if (dirty.security_deposit_before)
      body.security_deposit = toNum(data.security_deposit_before);
    if (dirty.security_deposit_after)
      body.security_deposit_after = toNum(data.security_deposit_after);
    if (dirty.initial_contract_start_date && data.initial_contract_start_date)
      body.start_date = data.initial_contract_start_date;
    if (dirty.rent_start_date)
      body.rent_start_date = data.rent_start_date || "";
    body.other_fees = otherFees
      .filter((f: any) => f.label?.trim())
      .map((f: any) => ({
        label: f.label,
        amount: f.amount ? Number(f.amount) : 0,
      }));

    const result = await dispatch(
      updateContract({ contract_id: targetContractId, body }) as any,
    );
    if (updateContract.fulfilled.match(result)) {
      await Promise.all([
        dispatch(fetchPropertySectionById({ property_section_id: sid }) as any),
        dispatch(fetchUnitById({ unit_id: sid }) as any),
        dispatch(
          fetchContractsByUnit({ unit_id: sid, status: "active" }) as any,
        ),
      ]);
      showToast({ type: "success", message: "契約条件を更新しました" });
      rentrollForm.reset(rentrollForm.getValues());
    } else {
      const apiMessage =
        (result?.payload as any)?.message ||
        (result?.payload as any)?.data?.message;
      showToast({
        type: "error",
        message: apiMessage || "契約条件の更新に失敗しました",
      });
    }
  };

  // 常時フォーム化に伴い、current（および occupancyStatus）が更新されたタイミングで
  // 契約者フォーム・レントロールフォームに最新値を流し込む。
  // 入居中（active 契約あり）のときのみ populate する。
  // activeContract (= /contracts レスポンス) は Unit より遅れて到着するため、
  // contract_id / updated_at の変化も deps に含めて再 populate を発火する。
  useEffect(() => {
    if (!current) return;
    if (view.occupancyStatus !== "occupied") return;
    populateContractorForm();
    populateRentrollForm();
    // populateContractorForm/populateRentrollForm は引数なしのクロージャのため依存に含めない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    current?.property_section_id,
    current?.updated_at,
    view.occupancyStatus,
    activeContract?.contract_id,
    activeContract?.updated_at,
  ]);

  // モーダルの開閉状態
  const [isNewContractOpen, setIsNewContractOpen] = useState(false);
  // 「既存顧客から」導線で選んだ顧客 (新規契約に紐づける customer_id ＋プリフィル)
  const [newContractCustomer, setNewContractCustomer] =
    useState<Customer | null>(null);
  const [isEditContractOpen, setIsEditContractOpen] = useState(false);
  const [isDepartureProcessOpen, setIsDepartureProcessOpen] = useState(false);
  const [isEditContractHistoryOpen, setIsEditContractHistoryOpen] =
    useState(false);
  const [editingContractData, setEditingContractData] = useState<any>(null);

  // 顧客マスタ波及の確認モーダル (氏名・連絡先編集が他の契約にも波及する場合)
  const [linkedConfirmOpen, setLinkedConfirmOpen] = useState(false);
  const [linkedConfirmLoading, setLinkedConfirmLoading] = useState(false);
  const [linkedContracts, setLinkedContracts] = useState<
    LinkedContractSummary[]
  >([]);
  // 確認モーダルで「変更する」を押したときに実行する保留アクション。
  // インライン編集・契約更新(renewal) いずれの確定処理も差し込めるよう関数で保持する。
  const [pendingLinkedAction, setPendingLinkedAction] = useState<{
    run: () => Promise<void>;
  } | null>(null);

  // ファイルプレビューモーダル用の状態
  const [previewFileOpen, setPreviewFileOpen] = useState(false);
  const [previewFileSrc, setPreviewFileSrc] = useState<string>("");
  const [previewFileName, setPreviewFileName] = useState<string>("");

  // 書類・履歴のローカル状態（アップロード反映用）
  const [documentsCount, setDocumentsCount] = useState<number>(
    view.documentsCount ?? 0,
  );
  const [history, setHistory] = useState<PrivateAreaHistoryRecord[]>(
    view.history || [],
  );
  const [expandedOthers, setExpandedOthers] = useState<Record<string, boolean>>(
    {},
  );
  const [expandedEditHistory, setExpandedEditHistory] = useState<
    Record<string, boolean>
  >({});
  const [expandedRevisionItems, setExpandedRevisionItems] = useState<
    Record<string, boolean>
  >({});
  const [deleteDoc, setDeleteDoc] = useState<{
    url: string;
    name: string;
  } | null>(null);
  const [openDeleteDocModal, setOpenDeleteDocModal] = useState(false);

  // 契約書類は Contract レコード (activeContract.documents) のみから組み立てる。
  // 区画書類 (Unit.documents) とは独立 — 用途が違うので混同しない。
  const contractDocuments = useMemo(() => {
    const docs: { name: string; url: string; category: string }[] = [];
    const list = (activeContract as any)?.documents as
      | Array<{
          document_id?: string;
          category?: string;
          label?: string;
          file_name?: string;
          s3_key?: string;
        }>
      | undefined;
    if (!Array.isArray(list)) return docs;
    // category code → 表示名 (個人 / 法人共通)
    const categoryLabel = (cat?: string): string => {
      switch (cat) {
        case "contract":
          return "賃貸借契約書";
        case "id":
          return "身分証明書";
        case "income":
          return "収入証明書";
        case "guarantor":
          return "保証人関連書類";
        case "registry":
          return "登記簿謄本";
        case "seal":
          return "印鑑証明書";
        case "articles":
          return "定款／会社概要";
        default:
          return "その他";
      }
    };
    for (const d of list) {
      const url = d?.s3_key;
      if (!url) continue;
      docs.push({
        name:
          d?.file_name || decodeDocumentNameFromUrl(String(url)) || String(url),
        url: String(url),
        category: d?.label || categoryLabel(d?.category),
      });
    }
    return docs;
  }, [activeContract]);

  const handleDownloadDocument = async (doc: {
    url: string;
    name?: string;
  }) => {
    if (!doc?.url) return;

    const resolvedName =
      doc.name || decodeDocumentNameFromUrl(doc.url) || "document";

    if (doc.url.startsWith("data:")) {
      const directLink = document.createElement("a");
      directLink.href = doc.url;
      directLink.download = resolvedName;
      document.body.appendChild(directLink);
      directLink.click();
      document.body.removeChild(directLink);
      return;
    }

    try {
      const apiBaseUrl = getApiBaseUrl();
      const token = getAuthToken();
      const s3Key = extractS3KeyFromUrl(doc.url);

      if (!s3Key) {
        const fallbackLink = document.createElement("a");
        fallbackLink.href = doc.url;
        fallbackLink.download = resolvedName;
        fallbackLink.target = "_blank";
        fallbackLink.rel = "noopener noreferrer";
        document.body.appendChild(fallbackLink);
        fallbackLink.click();
        document.body.removeChild(fallbackLink);
        return;
      }

      const response = await fetch(`${apiBaseUrl}/api/v1/download/file`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ key: s3Key }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch file for download");
      }

      const contentType =
        response.headers.get("content-type") ||
        getContentTypeFromFileName(resolvedName);
      const arrayBuffer = await response.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      const dataToDownload = processJsonResponse(uint8Array) ?? uint8Array;
      createBlobAndDownload(dataToDownload, contentType, resolvedName);
    } catch (error) {
      console.error("Error downloading document:", error);
      showToast({
        type: "error",
        message: "書類のダウンロードに失敗しました",
      });
    }
  };

  // ファイルプレビューを開く
  const handlePreviewDocument = (s3Key: string, fileName: string) => {
    if (!s3Key) return;

    // S3のURLをそのまま使う（署名付きURLなので直接アクセス可能）
    setPreviewFileSrc(s3Key);
    setPreviewFileName(fileName);
    setPreviewFileOpen(true);
  };

  // プレビューモーダルを閉じる
  const handleClosePreview = () => {
    setPreviewFileOpen(false);
    setPreviewFileSrc("");
    setPreviewFileName("");
  };

  // リロード時に取得データへ同期
  useEffect(() => {
    setHistory(view.history || []);
  }, [view.history]);

  useEffect(() => {
    setDocumentsCount(contractDocuments.length);
  }, [contractDocuments.length]);

  // モーダルハンドラー
  // 既存顧客 → 新規契約モーダルの契約者プリフィル。
  // Customer は契約者欄と同形 (姓/名・法人代表者) なのでフィールド1対1で転記。
  const customerToContractInitialData = (
    c: Customer | null,
  ): Record<string, unknown> | undefined => {
    if (!c) return undefined;
    if (c.customer_type === "corporate") {
      return {
        contractorType: "corporate",
        companyName: c.company_name || "",
        representativeLastName: c.representative_last_name || "",
        representativeFirstName: c.representative_first_name || "",
        companyPhone: c.phone || "",
        companyEmail: c.email || "",
      };
    }
    return {
      contractorType: "individual",
      tenantLastName: c.last_name || "",
      tenantFirstName: c.first_name || "",
      tenantPhone: c.phone || "",
      tenantEmail: c.email || "",
    };
  };

  const handleNewContractSubmit = async (data: any) => {
    const sid = params.privateAreaId;
    if (!sid) return;

    const toNum = (v: unknown): number | undefined => {
      if (v === undefined || v === null || v === "") return undefined;
      const n = typeof v === "number" ? v : Number(v);
      return Number.isFinite(n) ? n : undefined;
    };
    const isCorporate = data.contractorType === "corporate";
    const contractorName = isCorporate
      ? data.companyName || ""
      : [data.tenantLastName, data.tenantFirstName].filter(Boolean).join(" ");

    const body: any = {
      unit_id: sid,
      contractor_type: data.contractorType || "individual",
      contractor_name: contractorName,
      ...(isCorporate && { company_name: data.companyName || "" }),
      ...(isCorporate && {
        contact_person: [
          data.representativeLastName,
          data.representativeFirstName,
        ]
          .filter(Boolean)
          .join(" "),
      }),
      phone: isCorporate ? data.companyPhone : data.tenantPhone,
      email: isCorporate ? data.companyEmail : data.tenantEmail,
      start_date: data.startDate,
      end_date: data.endDate,
      rent_start_date: data.rentStartDate,
      monthly_rent: toNum(data.monthlyRent),
      pricing_type: data.pricingType,
      unit_price: toNum(data.unitPrice),
      quantity: toNum(data.quantity),
      quantity_unit: data.quantityUnit || undefined,
      rent_unit_price: toNum(data.rentUnitPrice),
      maintenance_fee: toNum(data.maintenanceFee),
      maintenance_fee_unit_price: toNum(data.maintenanceFeeUnitPrice),
      security_deposit: toNum(data.deposit),
      security_deposit_after: toNum(data.depositAfter),
      key_money: toNum(data.keyMoney),
      other_fees: (data.otherFees || [])
        .filter((f: any) => f.label?.trim())
        .map((f: any) => ({
          label: f.label,
          amount: f.amount ? Number(f.amount) : 0,
        })),
      contract_form: data.contract_form,
      contract_remarks: data.note,
      // 「既存顧客から」導線で選んだ顧客に紐づける (Contract.customer_id)
      ...(newContractCustomer
        ? { customer_id: newContractCustomer.customer_id }
        : {}),
    };

    const result = await dispatch(createContract(body) as any);
    if (createContract.fulfilled.match(result)) {
      // 区画情報側 (Unit.current_contract_id, occupancy_status) も更新されるので再取得
      await dispatch(fetchUnitById({ unit_id: sid }) as any);
      await dispatch(
        fetchContractsByUnit({ unit_id: sid, status: "active" }) as any,
      );
      dispatch(fetchContractHistoryByUnit({ unit_id: sid }) as any);
      showToast({ type: "success", message: "新規契約を登録しました" });
      setIsNewContractOpen(false);
      setNewContractCustomer(null);
    } else {
      const apiMessage =
        (result?.payload as any)?.message ||
        (result?.payload as any)?.data?.message;
      showToast({
        type: "error",
        message: apiMessage || "新規契約の登録に失敗しました",
      });
      throw new Error(apiMessage || "新規契約の登録に失敗しました");
    }
  };

  /**
   * 「契約更新」モーダルの送信ハンドラ。
   * 業務的には「既存契約を ended にして、新契約を active で発番」する renewal フロー。
   * BE 側 POST /contracts/:id/renew が 1 トランザクションで:
   *   - 旧 contract.status = "ended"
   *   - 新 contract を発番
   *   - Unit.current_contract_id を新 ID に置換
   *   - history を 2 件 (contract_end + renewal) で記録
   */
  const handleEditContractSubmit = async (
    data: any,
    // react-hook-form の handleSubmit は (data, event) で呼ぶため、確認スキップは
    // 確定ボタンからの明示 true のときだけ有効にする (Event は truthy)。
    skipLinkedConfirmArg: unknown = false,
  ) => {
    const skipLinkedConfirm = skipLinkedConfirmArg === true;
    const sid = params.privateAreaId;
    if (!sid) return;

    const currentContractId = (unit as any)?.current_contract_id;
    if (!currentContractId) {
      showToast({ type: "error", message: "更新元の契約が見つかりません" });
      return;
    }

    const toNum = (v: unknown): number | undefined => {
      if (v === undefined || v === null || v === "") return undefined;
      const n = typeof v === "number" ? v : Number(v);
      return Number.isFinite(n) ? n : undefined;
    };
    const isCorporate = data.contractorType === "corporate";
    const contractorName = isCorporate
      ? data.companyName || ""
      : [data.tenantLastName, data.tenantFirstName].filter(Boolean).join(" ");

    // 契約更新(renewal): 当該契約は「更新」扱い。ただし本人情報(氏名・連絡先)を
    // 現在値から変更した場合は、顧客マスタが正本なので他のアクティブ契約にも波及する。
    // 他契約は「編集」扱い (当該契約は renewal 済みなのでカスケードから除外する)。
    const customerId = (activeContract as any)?.customer_id as
      | string
      | undefined;
    const newPhone = isCorporate ? data.companyPhone : data.tenantPhone;
    const newEmail = isCorporate ? data.companyEmail : data.tenantEmail;
    const identityChanged =
      (contractorName || "") !== (activeContract?.contractor_name || "") ||
      (newPhone || "") !== (activeContract?.phone || "") ||
      (newEmail || "") !== (activeContract?.email || "");
    const propagateIdentity = !!customerId && identityChanged;

    if (!skipLinkedConfirm && propagateIdentity) {
      setLinkedConfirmLoading(true);
      try {
        const res = await dispatch(
          searchActiveContractsByCustomer({ customer_id: customerId! }) as any,
        );
        const linked = searchActiveContractsByCustomer.fulfilled.match(res)
          ? (res.payload as Array<any>)
          : [];
        const others = linked
          .filter((c) => c.contract_id !== currentContractId)
          .map((c) => ({
            contract_id: c.contract_id,
            property_name: c.property_name || "",
            area_type: c.unit_area_type || undefined,
            unit_name: c.unit_name || "",
          }));
        if (others.length > 0) {
          setLinkedContracts(others);
          setPendingLinkedAction({
            run: async () => {
              await handleEditContractSubmit(data, true);
            },
          });
          setLinkedConfirmOpen(true);
          // "deferred": 契約更新モーダルは閉じず、確認モーダルを上に重ねる。
          // 確定後に skipLinkedConfirm=true で再実行する。
          return "deferred" as const;
        }
      } finally {
        setLinkedConfirmLoading(false);
      }
    }

    const body: any = {
      contractor_type: data.contractorType || "individual",
      contractor_name: contractorName,
      ...(isCorporate && { company_name: data.companyName || "" }),
      ...(isCorporate && {
        contact_person: [
          data.representativeLastName,
          data.representativeFirstName,
        ]
          .filter(Boolean)
          .join(" "),
      }),
      phone: isCorporate ? data.companyPhone : data.tenantPhone,
      email: isCorporate ? data.companyEmail : data.tenantEmail,
      start_date: data.startDate,
      end_date: data.endDate,
      rent_start_date: data.rentStartDate,
      cancellation_date: data.cancellationDate,
      monthly_rent: toNum(data.monthlyRent),
      pricing_type: data.pricingType,
      unit_price: toNum(data.unitPrice),
      quantity: toNum(data.quantity),
      quantity_unit: data.quantityUnit || undefined,
      rent_unit_price: toNum(data.rentUnitPrice),
      maintenance_fee: toNum(data.maintenanceFee),
      maintenance_fee_unit_price: toNum(data.maintenanceFeeUnitPrice),
      security_deposit: toNum(data.deposit),
      security_deposit_after: toNum(data.depositAfter),
      key_money: toNum(data.keyMoney),
      other_fees: (data.otherFees || [])
        .filter((f: any) => f.label?.trim())
        .map((f: any) => ({
          label: f.label,
          amount: f.amount ? Number(f.amount) : 0,
        })),
      contract_form: data.contract_form,
      contract_remarks: data.note,
    };

    const result = await dispatch(
      renewContract({ contract_id: currentContractId, body }) as any,
    );
    if (!renewContract.fulfilled.match(result)) {
      const apiMessage =
        (result?.payload as any)?.message ||
        (result?.payload as any)?.data?.message;
      showToast({
        type: "error",
        message: apiMessage || "契約の更新に失敗しました",
      });
      throw new Error(apiMessage || "契約の更新に失敗しました");
    }

    // 本人情報を変更した場合: 顧客マスタへ反映し、他のアクティブ契約に「編集」として
    // 波及させる。当該契約は renewal で「更新」処理済みのため exclude_contract_id で除外。
    if (propagateIdentity) {
      const customerBody = isCorporate
        ? {
            customer_type: "corporate" as const,
            company_name: data.companyName || "",
            representative_last_name: data.representativeLastName || "",
            representative_first_name: data.representativeFirstName || "",
            phone: data.companyPhone || "",
            email: data.companyEmail || "",
          }
        : {
            customer_type: "individual" as const,
            last_name: data.tenantLastName || "",
            first_name: data.tenantFirstName || "",
            phone: data.tenantPhone || "",
            email: data.tenantEmail || "",
          };
      const custRes = await dispatch(
        updateCustomer({
          customer_id: customerId!,
          body: { ...customerBody, exclude_contract_id: currentContractId },
        }) as any,
      );
      if (!updateCustomer.fulfilled.match(custRes)) {
        showToast({
          type: "error",
          message: "他の契約への反映に失敗しました",
        });
      }
    }

    // Unit (current_contract_id 更新済) と契約一覧 / 履歴を再取得
    await dispatch(fetchUnitById({ unit_id: sid }) as any);
    await dispatch(
      fetchContractsByUnit({ unit_id: sid, status: "active" }) as any,
    );
    dispatch(fetchContractHistoryByUnit({ unit_id: sid }) as any);
    showToast({ type: "success", message: "契約を更新しました" });
    // 確認モーダル経由 (skip=true) の場合、契約更新モーダルは自動で閉じないので明示的に閉じる。
    // (通常経路は EditContractModal 側が onSubmit 解決後に閉じる)
    if (skipLinkedConfirm) setIsEditContractOpen(false);
  };

  const handleDepartureProcessSubmit = async (
    data: DepartureProcessFormData,
  ) => {
    const sid = params.privateAreaId;
    if (!sid) return;
    const currentContractId = (unit as any)?.current_contract_id;
    if (!currentContractId) {
      showToast({ type: "error", message: "退去対象の契約が見つかりません" });
      return;
    }

    const result = await dispatch(
      endContract({
        contract_id: currentContractId,
        departure_date: data.departureDate,
        departure_reason: data.departureReason || "",
        contract_remarks: data.note || "",
      }) as any,
    );
    if (endContract.fulfilled.match(result)) {
      await dispatch(fetchUnitById({ unit_id: sid }) as any);
      await dispatch(
        fetchContractsByUnit({ unit_id: sid, status: "active" }) as any,
      );
      dispatch(fetchContractHistoryByUnit({ unit_id: sid }) as any);
      showToast({ type: "success", message: "退去処理が完了しました" });
    } else {
      const apiMessage =
        (result?.payload as any)?.message ||
        (result?.payload as any)?.data?.message;
      showToast({
        type: "error",
        message: apiMessage || "退去処理に失敗しました",
      });
      throw new Error(apiMessage || "退去処理に失敗しました");
    }
  };

  const handleConfirmDeleteDocument = async () => {
    const sid = params.privateAreaId;
    if (!sid || !deleteDoc?.url) return;
    const s3Key = extractS3KeyFromUrl(deleteDoc.url);
    if (!s3Key) {
      setOpenDeleteDocModal(false);
      setDeleteDoc(null);
      return;
    }
    try {
      const result = await (dispatch(
        deletePropertySectionDocument({
          property_section_id: sid,
          s3_key: s3Key,
          file_name: deleteDoc.name,
        }) as any,
      ) as any);
      if ((deletePropertySectionDocument as any).fulfilled.match(result)) {
        await dispatch(
          fetchPropertySectionById({ property_section_id: sid }) as any,
        );
        showToast({ type: "success", message: "書類を削除しました" });
      } else {
        showToast({ type: "error", message: "書類の削除に失敗しました" });
      }
    } finally {
      setOpenDeleteDocModal(false);
      setDeleteDoc(null);
    }
  };

  /**
   * 履歴編集モーダルの送信ハンドラ — 既存契約レコードの訂正。
   * 旧 PropertySection 互換のフィールド名で送信されてくるが、
   * 新 Contracts テーブル (PATCH /contracts/:id) 用の body にマッピングして 1 リクエストで保存する。
   */
  const handleEditContractHistorySubmit = (data: any) => {
    const sid = params.privateAreaId;
    if (!sid || !editingContractData) return;

    const contractorInfoChanged =
      data.contractorType === "individual"
        ? data.tenantLastName !== editingContractData.originalTenantLastName ||
          data.tenantFirstName !==
            editingContractData.originalTenantFirstName ||
          data.tenantPhone !== editingContractData.originalTenantPhone ||
          data.tenantEmail !== editingContractData.originalTenantEmail
        : data.companyName !== editingContractData.originalCompanyName ||
          data.representativeLastName !==
            editingContractData.originalRepresentativeLastName ||
          data.representativeFirstName !==
            editingContractData.originalRepresentativeFirstName ||
          data.companyPhone !== editingContractData.originalCompanyPhone ||
          data.companyEmail !== editingContractData.originalCompanyEmail;

    const contractConditionChanged =
      data.startDate !== editingContractData.originalStartDate ||
      data.endDate !== editingContractData.originalEndDate ||
      String(data.monthlyRent) !== editingContractData.originalMonthlyRent ||
      String(data.rentUnitPrice) !==
        String(editingContractData.rentUnitPrice ?? "") ||
      String(data.maintenanceFeeUnitPrice) !==
        String(editingContractData.maintenanceFeeUnitPrice ?? "") ||
      String(data.deposit) !== editingContractData.originalDeposit ||
      String(data.keyMoney) !== editingContractData.originalKeyMoney ||
      data.note !== editingContractData.originalNote;

    if (!contractorInfoChanged && !contractConditionChanged) {
      showToast({ type: "info", message: "変更がありません" });
      return;
    }

    // 編集対象 contract_id の決定 (両者が同一でない可能性も考慮し、まず contractorInfoId、なければ conditionId)
    const targetContractId =
      editingContractData.contractorInfoId ||
      editingContractData.contractConditionId ||
      ((unit as any)?.current_contract_id ?? null);
    if (!targetContractId) {
      showToast({ type: "error", message: "編集対象の契約が見つかりません" });
      return;
    }

    const isCorporate = data.contractorType === "corporate";
    const contractorName = isCorporate
      ? data.companyName || ""
      : `${data.tenantLastName ?? ""} ${data.tenantFirstName ?? ""}`.trim();
    const contactPerson = isCorporate
      ? `${data.representativeLastName ?? ""} ${data.representativeFirstName ?? ""}`.trim()
      : undefined;
    const phone = isCorporate ? data.companyPhone : data.tenantPhone;
    const email = isCorporate ? data.companyEmail : data.tenantEmail;
    const toNum = (v: unknown): number | undefined =>
      v === undefined || v === null || v === "" ? undefined : Number(v);

    const body: any = {};
    if (contractorInfoChanged) {
      body.contractor_type = data.contractorType;
      body.contractor_name = contractorName;
      if (isCorporate) {
        body.company_name = data.companyName || "";
        body.contact_person = contactPerson || "";
      }
      if (phone !== undefined) body.phone = phone || "";
      if (email !== undefined) body.email = email || "";
    }
    if (contractConditionChanged) {
      body.start_date = data.startDate;
      body.end_date = data.endDate;
      body.monthly_rent = toNum(data.monthlyRent);
      body.pricing_type = data.pricingType;
      body.unit_price = toNum(data.unitPrice);
      body.quantity = toNum(data.quantity);
      body.quantity_unit = data.quantityUnit || undefined;
      body.rent_unit_price = toNum(data.rentUnitPrice);
      body.maintenance_fee_unit_price = toNum(data.maintenanceFeeUnitPrice);
      body.security_deposit = toNum(data.deposit);
      body.key_money = toNum(data.keyMoney);
      body.contract_remarks = data.note;
    }

    const updates = [
      dispatch(updateContract({ contract_id: targetContractId, body }) as any),
    ];

    Promise.all(updates).then((results) => {
      const allSuccess = results.every((result) =>
        updateContract.fulfilled.match(result),
      );

      if (allSuccess) {
        dispatch(
          fetchContractsByUnit({ unit_id: sid, status: "active" }) as any,
        );
        dispatch(fetchContractHistoryByUnit({ unit_id: sid }) as any);
        dispatch(fetchPropertySectionById({ property_section_id: sid }) as any);
        showToast({ type: "success", message: "契約情報を編集しました" });
        setIsEditContractHistoryOpen(false);
        setEditingContractData(null);
      } else {
        showToast({ type: "error", message: "契約情報の編集に失敗しました" });
      }
    });
  };

  const hasPrivateAreaDataForError = Boolean(current?.property_section_id);
  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasPrivateAreaDataForError,
    dedupeKey: `private-area:${params.propertyId ?? ""}:${params.privateAreaId ?? ""}`,
  });

  if (hasStrictlyInvalidTabQuery) {
    return <InitialLoadError errorType="notFound" />;
  }

  if (
    shouldShowInitialLoadError(initialLoadError, hasPrivateAreaDataForError)
  ) {
    return (
      <InitialLoadError errorType={initialLoadError} onRetry={fetchDetail} />
    );
  }

  if (!embedded && hasPrivateAreaDataForError && !hasValidTabQuery) {
    return <InitialLoadError errorType="notFound" />;
  }

  return (
    // MainLayout (DashboardLayout) の Grid item が pl: 4 (32px) / pr: 3 (24px)
    // を当てているため、ここは負の ml で左側のみ寄せて横幅を拡張する。
    // (ml のみ。mr は触らないので右端はレイアウト側の余白に揃ったまま)
    // タイトル部分は負マージンを相殺して元位置に戻す。
    //
    // 物件詳細ページと同様「タブ枠固定・中身だけ内部スクロール」にするため、
    // ページ全体を flex 列 + overflow:hidden で包み、各タブが flex:1 で残りを
    // 埋める構成にする。embedded (PreviewDrawer 等) のときは親のスクロールに
    // 委ねるため固定高さを掛けない。
    <Box
      // 埋め込み(プレビュードロワー)時は sticky ヘッダーの上に余白があると
      // スクロール開始時にその分ヘッダーごと上へ流れて見えるため pt:0。
      pt={embedded ? 0 : 0.5}
      data-entity="unit"
      data-id={params.privateAreaId || ""}
      sx={{
        // lg未満: Layout pl=24 - 8 = 16px
        // lg以上: Layout pl=32 - 16 = 16px
        ml: embedded ? 0 : { xs: -1, lg: -2 },
        ...(embedded
          ? activeTab === "tenant"
            ? {
                // テナント管理タブだけは内側のグレー背景を drawer 下端まで
                // 伸ばしたいので、非埋め込みと同じ flex column チェーンを
                // 組む (drawer 中身の Box が flex:1 で高さを持っているため
                // height:100% で flex 解決される)。
                // overflow:hidden にすると tenant Box の負マージン (左右
                // ブリード) がここで clip されるため visible のままにする。
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "visible",
              }
            : { pb: { xs: 2, md: 8 } }
          : {
              // 600px 未満=スマホヘッダー(67) / 600px 以上=デスクトップヘッダー(50)。縮小レール導入に合わせ sm 境界で切替。
              height: { xs: "calc(100vh - 67px)", sm: "calc(100vh - 50px)" },
              display: "flex",
              flexDirection: "column",
              // overflow:hidden にすると子の負マージン (グレー背景の左右
              // ブリード) がここで clipping されてしまうため visible のままに
              // する。body は useEffect で overflow:hidden に lock 済み。
              overflow: "visible",
            }),
      }}
    >
      {/* ヘッダ (タイトル + バッジ + 物件名 + 親タブ) を sticky で固定。
          スクロール時もタイトル〜タブが常に上部に残るようにする。
          B7 (C-8) で `DetailPageHeader` に切り出し。 */}
      <DetailPageHeader
        embedded={embedded}
        title={privateAreaName}
        propertyName={view.propertyName || propertyName}
        isAncillary={isAncillary}
        areaType={(current as any)?.area_type}
        tabs={TAB_CONFIG}
        activeTabIndex={TAB_CONFIG.findIndex((tab) => tab.id === activeTab)}
        onTabChange={handleTabChange}
      />

      {/* 顧客マスタ (氏名・連絡先) 編集が他のアクティブ契約にも波及する確認 */}
      <LinkedCustomerEditConfirmModal
        open={linkedConfirmOpen}
        linkedContracts={linkedContracts}
        loading={linkedConfirmLoading}
        onCancel={handleLinkedCancel}
        onConfirm={handleLinkedConfirm}
      />

      {/* B7 (C-8) でモーダル群を `DetailPageModals` に集約 */}
      <DetailPageModals
        view={view as any}
        current={current}
        isAncillary={isAncillary}
        areaTsubo={areaTsubo}
        isNewContractOpen={isNewContractOpen}
        setIsNewContractOpen={(v) => {
          setIsNewContractOpen(v);
          if (!v) setNewContractCustomer(null);
        }}
        handleNewContractSubmit={handleNewContractSubmit}
        newContractInitialData={customerToContractInitialData(
          newContractCustomer,
        )}
        isEditContractOpen={isEditContractOpen}
        setIsEditContractOpen={setIsEditContractOpen}
        handleEditContractSubmit={handleEditContractSubmit}
        isDepartureProcessOpen={isDepartureProcessOpen}
        setIsDepartureProcessOpen={setIsDepartureProcessOpen}
        handleDepartureProcessSubmit={handleDepartureProcessSubmit}
        isEditContractHistoryOpen={isEditContractHistoryOpen}
        setIsEditContractHistoryOpen={setIsEditContractHistoryOpen}
        editingContractData={editingContractData}
        setEditingContractData={setEditingContractData}
        handleEditContractHistorySubmit={handleEditContractHistorySubmit}
        openDeleteDocModal={openDeleteDocModal}
        setOpenDeleteDocModal={setOpenDeleteDocModal}
        deleteDoc={deleteDoc}
        handleConfirmDeleteDocument={handleConfirmDeleteDocument}
      />

      {/* タブコンテンツ。
          embedded 以外ではページ全体が flex 列で固定高さされているため、
          各タブは flex:1 + minHeight:0 で残り領域を埋め、必要に応じて
          自前で overflow:auto を持つ。
          shell 自体は overflow:visible にしてグレー背景の左右ブリードを
          切らないようにする。 */}
      <Box
        sx={{
          ...(embedded
            ? activeTab === "tenant"
              ? {
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }
              : {}
            : {
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "visible",
              }),
        }}
      >
        {/* ローディング中はヘッダー (DetailPageHeader) を残したまま、コンテンツ領域を
            CustomLoading に差し替える (他の修繕一覧/見積もり一覧/入金管理と同じ挙動)。 */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
              minHeight: "60vh",
              width: "100%",
            }}
          >
            <CustomLoading loading={true} fullScreen={false} size="medium" />
          </Box>
        ) : null}

        {/* 区画詳細 */}
        {!loading && activeTab === "section" && params.privateAreaId && (
          <Box
            sx={{
              ...(embedded
                ? {}
                : {
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    pb: { xs: 2, md: 8 },
                  }),
            }}
          >
            <SectionTabForm
              sectionId={params.privateAreaId}
              current={current}
              isAncillary={isAncillary}
              dispatch={dispatch}
              occupancyStatus={
                view?.occupancyStatus as "occupied" | "vacant" | undefined
              }
              embedded={embedded}
              onDirtyChange={setSectionDirty}
            />
          </Box>
        )}

        {/* テナント管理（親タブ）→ サブタブ: 契約情報 / 入金管理
            UI は概要タブ・支出まとめと同じ PropertyRegistrationTabs (カード風タブ)。
            外側はグレー背景で包み、左右は画面端まで伸ばす（pages/properties/Detail
            の概要/収支 TabPanel と同じ流儀）。
            タブ枠は flex:1 で残り領域を埋め、内部スクロールは
            PropertyRegistrationTabs `scrollableContent` が担う。 */}
        {!loading && activeTab === "tenant" && (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              // 非埋め込み: 親 Box が ml: -1(xs) / -2(lg) で既に Layout padding を
              // 8/16 食い込ませているため、ここでは ml: -2 で残り分 (16px) を打ち消す。
              // 埋め込み (PreviewDrawer): drawer 中身が px: 1.5 (12px) を持つので
              // それを打ち消してグレーを drawer 端まで伸ばす。
              ml: embedded ? -1.5 : -2,
              mr: embedded ? -1.5 : -3,
              pt: 1.7,
              // 内側 padding 16px で中身を 16px から開始 (PropertyDetailPage と統一)
              px: 2,
              pb: 0,
              // 非埋め込み・埋め込みどちらでも、祖先 (outer + 中間 wrapper)
              // が flex column + 高さ確保済みなので、ここは flex:1 で残り
              // 高さを全部食ってグレー背景を下端まで伸ばす。
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <PropertyRegistrationTabs
              currentTab={TENANT_SUB_TAB_CONFIG.findIndex(
                (t) => t.id === activeTenantSubTab,
              )}
              tabs={TENANT_SUB_TAB_CONFIG.map((t, idx) => ({
                id: idx,
                label: t.label,
              }))}
              onTabChange={(newValue: number) =>
                handleTenantSubTabChange(newValue)
              }
              disableConfirm
              scrollableContent
            >
              {contractsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1,
                    minHeight: "60vh",
                    width: "100%",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                  }}
                >
                  <CustomLoading
                    loading={true}
                    fullScreen={false}
                    size="medium"
                  />
                </Box>
              ) : (
                <>
                  {activeTenantSubTab === "contract-info" && (
                    // 下部 FixedActionBar の保存ボタンと被らないようスクロール末尾に余白
                    <Box sx={{ pb: 10 }}>
                      <ContractorTab
                        view={view}
                        current={current}
                        isAncillary={isAncillary}
                        areaTsubo={areaTsubo}
                        contractorForm={contractorForm}
                        rentrollForm={rentrollForm}
                        documentsCount={documentsCount ?? 0}
                        contractDocuments={contractDocuments}
                        handleContractorInlineSubmit={
                          handleContractorInlineSubmit
                        }
                        handleRentrollInlineSubmit={handleRentrollInlineSubmit}
                        setIsNewContractOpen={setIsNewContractOpen}
                        onNewContractBlank={() => {
                          setNewContractCustomer(null);
                          setIsNewContractOpen(true);
                        }}
                        onNewContractFromCustomer={(c) => {
                          setNewContractCustomer(c);
                          setIsNewContractOpen(true);
                        }}
                        setIsEditContractOpen={setIsEditContractOpen}
                        setIsDepartureProcessOpen={setIsDepartureProcessOpen}
                        handleDownloadDocument={handleDownloadDocument}
                        otherFees={otherFees}
                        setOtherFees={setOtherFees}
                        embedded={embedded}
                      />
                    </Box>
                  )}

                  {activeTenantSubTab === "payment" && (
                    <Box sx={{ mt: 1, pb: 10 }}>
                      <CustomSectionTitle title="入金管理" addBorder />
                      <Box
                        sx={{
                          background: "#fff",
                          pt: 1,
                          pb: 3,
                        }}
                      >
                        <PaymentHistoryTab
                          propertySectionId={params.privateAreaId || ""}
                          propertyId={params.propertyId || ""}
                          propertyName={propertyName}
                          unitName={
                            view.privateAreaName || current?.landmark_name
                          }
                          sectionData={current}
                          revenueGroup={
                            unit?.revenue_type === "stock_rent"
                              ? "stock_rent"
                              : unit?.revenue_type === "stock_other"
                                ? "stock_other"
                                : unit?.revenue_type === "none"
                                  ? "other"
                                  : undefined
                          }
                        />
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </PropertyRegistrationTabs>
          </Box>
        )}

        {/* 予算（一旦コメントアウト・必要時に復活）
        {!loading && activeTab === "budget" && (
          <Box sx={{ mt: 3 }}>
            <RentRollCard isAncillary={isAncillary} />
          </Box>
        )}
        */}

        {/* 工事履歴 */}
        {!loading && activeTab === "repair" && (
          <Box
            sx={{
              ...(embedded
                ? { mt: 2 }
                : {
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    mt: 2,
                    pb: { xs: 2, md: 8 },
                  }),
            }}
          >
            <CustomSectionTitle title="工事履歴" addBorder />
            <Box
              sx={{
                background: "#fff",
                pt: 1,
                pb: embedded ? 8 : 0,
              }}
            >
              <RepairsSectionInline
                propertyId={params.propertyId}
                // この詳細ページは全 area_type の Unit で共有されている (route コメント参照)。
                // 場所区分は Unit データから動的に取得 (private/common/facility/land/other)。
                defaultScope={(current as any)?.area_type ?? "private"}
                defaultAreaName={view.privateAreaName}
                propertyName={propertyName}
                privateAreaId={params.privateAreaId}
                privateAreaName={privateAreaName}
              />
            </Box>
          </Box>
        )}

        {/* 契約履歴 (トップレベルタブ — 工事履歴の右) */}
        {!loading && activeTab === "contract" && (
          <Box
            sx={{
              ...(embedded
                ? {}
                : {
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    pb: { xs: 2, md: 8 },
                  }),
            }}
          >
            <ContractHistoryTab
              history={history}
              expandedOthers={expandedOthers}
              setExpandedOthers={setExpandedOthers}
              expandedEditHistory={expandedEditHistory}
              setExpandedEditHistory={setExpandedEditHistory}
              expandedRevisionItems={expandedRevisionItems}
              setExpandedRevisionItems={setExpandedRevisionItems}
              handlePreviewDocument={handlePreviewDocument}
              unitId={params.privateAreaId}
              hasMore={contractHistoryHasMore}
              loading={contractHistoryLoading}
              isLoadingMore={contractHistoryLoadingMore}
              onLoadMore={handleLoadMoreContractHistory}
            />
          </Box>
        )}
      </Box>

      {/* ファイルプレビューモーダル */}
      <CustomFileModal
        open={previewFileOpen}
        onClose={handleClosePreview}
        imageSrc={previewFileSrc}
        imageName={previewFileName}
      />
    </Box>
  );
};

export default PrivateAreaDetailPage;
